import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const DEFAULTS = {
  parallel: 't-fade',
  back: 'scroll-x-transition',
  forward: 'scroll-x-reverse-transition',
}

// Module-scoped singletons shared across every layout that calls useRouteTransition.
// One transition is happening at a time globally, so a single source of truth lets
// any layout's @after-leave drain cleanups queued from anywhere in the app.
const transitionName = ref(null)
const transitioning = ref(false)
const pendingQueue = []
let fallbackTimer = null
const FALLBACK_MS = 500

function flushQueue() {
  if (fallbackTimer) {
    clearTimeout(fallbackTimer)
    fallbackTimer = null
  }
  while (pendingQueue.length) {
    const fn = pendingQueue.shift()
    try {
      fn()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useRouteTransition] deferred callback threw', e)
    }
  }
}

/**
 * Defer a side-effect (typically state clearing) until the current page-leave
 * transition completes. Drained at @after-leave — the old DOM is gone but the
 * new component has not yet run setup(), so order is: old cleans → new mounts.
 *
 * If no transition starts within FALLBACK_MS (same-route or aborted nav),
 * the queue is flushed by the safety timer.
 */
export function runAfterTransition(fn) {
  if (typeof fn !== 'function') return
  pendingQueue.push(fn)
  if (!transitioning.value && !fallbackTimer) {
    fallbackTimer = setTimeout(flushQueue, FALLBACK_MS)
  }
}

export function onBeforeLeave() {
  transitioning.value = true
}

export function onAfterLeave() {
  flushQueue()
}

export function onAfterEnter() {
  transitioning.value = false
}

/**
 * Composable for handling route transitions
 * Replaces the Vue 2 RouteTransition mixin
 *
 * @returns {Object} Route transition state and methods
 */
export function useRouteTransition() {
  const route = useRoute()

  watch(route, (to, from) => {
    transitionName.value = getRouteTransition(to, from)
  })

  /**
   * Get the appropriate transition based on routes
   *
   * @param {Object} to - Target route
   * @param {Object} from - Source route
   * @returns {String} Transition name to use
   */
  const getRouteTransition = (to, from) => {
    let transition = null

    // Check for transition in route meta
    for (const r of to.matched) {
      if (r.meta.transition) transition = r.meta.transition
    }

    // Execute transition function if provided
    if (typeof transition === 'function') {
      transition = transition({
        transitionName: transitionName.value,
        transitioning: transitioning.value
      })
    }

    // Return directly if transition is a string
    if (typeof transition === 'string') {
      return transition
    }

    // Determine transition based on path depth
    const toDepth = to.path.split('/').length
    const fromDepth = from.path.split('/').length

    // Merge with defaults
    transition = {
      ...DEFAULTS,
      ...transition,
    }

    // Choose transition based on depth comparison
    if (toDepth === fromDepth) {
      return transition.parallel
    }

    return toDepth < fromDepth ? transition.back : transition.forward
  }

  return {
    transitionName,
    transitioning,
    getRouteTransition,
    onBeforeLeave,
    onAfterLeave,
    onAfterEnter,
    runAfterTransition,
  }
}
